import useGetNTSASummary, {
  NTSASummary,
  NTSASummaryParams,
} from 'hooks/ntsa/Usegetntsasummary';
import { ArrowRight, Calendar, RefreshCw } from 'lucide-react';
import moment from 'moment';
import React, { useState } from 'react';

export type DateRange = NTSASummaryParams;

export interface SummaryCardConfig {
  label: string;
  value: number;
  sublabel?: string;
  icon: React.ReactNode;
  iconBg: string;
  valueColor: string;
  borderColor: string;
  clickable?: boolean;
  onClick?: () => void;
}

export interface BreakdownItem {
  dot: string;
  label: string;
  value: number;
  pctVal: number;
}

export interface BreakdownGroup {
  title?: string;
  trackBg: string;
  segments: {
    color: string;
    width: number;
    left?: number;
  }[];
  items: BreakdownItem[];
}

interface SummaryDashboardProps {
  title: string;
  subtitle: string;
  logo?: {
    src: string;
    alt: string;
  };
  showWatermark?: boolean;
  cardColumns: string;
  queryParams?: Pick<NTSASummaryParams, 'cameraType'>;
  headerControls?: React.ReactNode;
  dateInputType?: 'date' | 'datetime-local';
  buildCards: (
    summary: NTSASummary | undefined,
    helpers: { pct: typeof pct; range: DateRange },
  ) => SummaryCardConfig[];
  buildBreakdownGroups: (
    summary: NTSASummary,
    helpers: { pct: typeof pct },
  ) => BreakdownGroup[];
  children?: (props: { range: DateRange }) => React.ReactNode;
}

const fmt = (n: number | undefined | null) => (n ?? 0).toLocaleString();

export const pct = (
  part: number | undefined | null,
  total: number | undefined | null,
) => {
  const safePart = part ?? 0;
  const safeTotal = total ?? 0;
  return safeTotal === 0 ? 0 : Math.round((safePart / safeTotal) * 100);
};

const StatCard: React.FC<SummaryCardConfig> = ({
  label,
  value,
  sublabel,
  icon,
  iconBg,
  valueColor,
  borderColor,
  clickable,
  onClick,
}) => (
  <button
    onClick={onClick}
    disabled={!clickable}
    className={`group w-full text-left bg-white rounded-2xl border-2 p-6 transition-all duration-200 ${borderColor} ${
      clickable
        ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0'
        : 'cursor-default'
    }`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
          {label}
        </p>
        <p className={`text-4xl font-bold tabular-nums ${valueColor}`}>
          {fmt(value)}
        </p>
        {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
      </div>
      <div className={`shrink-0 p-3 rounded-xl ${iconBg}`}>{icon}</div>
    </div>
    {clickable && (
      <div className="mt-4 flex items-center gap-1 text-xs font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
        View fines{' '}
        <ArrowRight
          size={12}
          className="group-hover:translate-x-0.5 transition-transform"
        />
      </div>
    )}
  </button>
);

const CardSkeleton = () => (
  <div className="h-40 bg-white rounded-2xl border-2 border-gray-100 animate-pulse" />
);

const SummaryDashboard: React.FC<SummaryDashboardProps> = ({
  title,
  subtitle,
  logo,
  showWatermark,
  cardColumns,
  queryParams,
  headerControls,
  dateInputType = 'date',
  buildCards,
  buildBreakdownGroups,
  children,
}) => {
  const dateValueFormat =
    dateInputType === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD';
  const [dateRange, setDateRange] = useState<DateRange>({
    from: moment().subtract(30, 'days').format(dateValueFormat),
    to: moment().format(dateValueFormat),
  });
  const [appliedRange, setAppliedRange] = useState<DateRange>(dateRange);

  const {
    data: summary,
    isLoading,
    isFetching,
    refetch,
  } = useGetNTSASummary({ ...appliedRange, ...queryParams });

  const cards = buildCards(summary, { pct, range: appliedRange });
  const breakdownGroups = summary ? buildBreakdownGroups(summary, { pct }) : [];

  const content = (
    <div className="relative z-0 bg-gray-50 min-h-full">
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="max-w-8xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {logo && (
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-10 w-10 object-contain"
              />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                {title}
              </h1>
              <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {headerControls}
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <Calendar size={14} className="text-gray-400 shrink-0" />
              <input
                type={dateInputType}
                value={dateRange.from}
                max={dateRange.to}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, from: e.target.value }))
                }
                className="text-sm bg-transparent border-none outline-none text-gray-700 w-44"
              />
              <span className="text-gray-300 text-xs">to</span>
              <input
                type={dateInputType}
                value={dateRange.to}
                min={dateRange.from}
                max={moment().format(dateValueFormat)}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, to: e.target.value }))
                }
                className="text-sm bg-transparent border-none outline-none text-gray-700 w-44"
              />
            </div>
            <button
              onClick={() => {
                if (dateRange.from && dateRange.to) setAppliedRange(dateRange);
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh"
              className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={isFetching ? 'animate-spin' : ''}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 py-8 space-y-8">
        <p className="text-xs text-gray-400 -mb-4">
          Showing data for{' '}
          <span className="font-medium text-gray-600">
            {moment(appliedRange.from).format('D MMM YYYY')}
          </span>{' '}
          -{' '}
          <span className="font-medium text-gray-600">
            {moment(appliedRange.to).format('D MMM YYYY')}
          </span>
        </p>

        {isLoading ? (
          <div className={`grid grid-cols-1 ${cardColumns} gap-5`}>
            {cards.map((card) => (
              <CardSkeleton key={card.label} />
            ))}
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${cardColumns} gap-5`}>
            {cards.map((card) => (
              <StatCard key={card.label} {...card} />
            ))}
          </div>
        )}

        {!isLoading && summary && (
          <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
              SMS Status Breakdown
            </h2>

            <div className="space-y-5">
              {breakdownGroups.map((group, index) => (
                <div key={group.title ?? index}>
                  {group.title && (
                    <p className="text-xs font-medium text-gray-400 mb-3">
                      {group.title}
                    </p>
                  )}
                  <div
                    className={`relative h-4 rounded-full ${group.trackBg} overflow-hidden mb-3`}
                  >
                    {group.segments.map((segment) => (
                      <div
                        key={`${segment.color}-${segment.left ?? 0}`}
                        className={`absolute top-0 h-full ${segment.color} transition-all duration-700 ease-out`}
                        style={{
                          left: `${segment.left ?? 0}%`,
                          width: `${segment.width}%`,
                        }}
                      />
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-6 text-sm">
                    {group.items.map(({ dot, label, value, pctVal }) => (
                      <div key={label} className="flex items-center gap-2">
                        <span
                          className={`inline-block w-3 h-3 rounded-full ${dot}`}
                        />
                        <span className="text-gray-600">
                          {label} -{' '}
                          <span className="font-semibold text-gray-900">
                            {fmt(value)}
                          </span>
                          <span className="text-gray-400 ml-1">
                            ({pctVal}%)
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {children?.({ range: appliedRange })}
      </div>
    </div>
  );

  if (!showWatermark) return content;

  return (
    <div className="relative">
      <div className="absolute inset-0 z-10 grid grid-cols-4 gap-8 opacity-[0.1] pointer-events-none">
        {Array.from({ length: 50 }).map((_, idx) => (
          <div key={idx} className="flex items-center justify-center">
            <img src={logo?.src} alt="" className="w-16 h-16 object-contain" />
          </div>
        ))}
      </div>
      {content}
    </div>
  );
};

export default SummaryDashboard;
